import {
  Box,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Flex,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useApiService } from "../../redux-action/api.service";
import { User } from "../../types/user-types";
import ContentCard from "../nav/ContentCard";
import { dateToString } from "../../common/utils/date-time";
import { SearchIcon } from "@chakra-ui/icons";

export const ManageCharactersUsersDashboard = () => {
  const api = useApiService();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Constants for pagination
  const ITEMS_PER_PAGE = 10;

  // Calculate statistics
 

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    const searchTerm = searchQuery.toLowerCase();
    return (
      user.firstName.toLowerCase().includes(searchTerm) ||
      user.lastName.toLowerCase().includes(searchTerm) ||
      user.email.toLowerCase().includes(searchTerm) ||
      (user.accountType || "").toLowerCase().includes(searchTerm) ||
      (user.isActive ? "active" : "inactive").includes(searchTerm)
    );
  });

  // Update getPaginatedData to use filtered users
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, endIndex);
  };

  // Update statistics to use filtered data when searching
  const totalUsersFiltered = searchQuery ? filteredUsers.length : users.length;
  const subscribedUsersFiltered = (searchQuery ? filteredUsers : users).filter(
    (user) => user.credit && user.credit > 0,
  ).length;
  const unsubscribedUsersFiltered =
    totalUsersFiltered - subscribedUsersFiltered;
  const newUsersFiltered = (searchQuery ? filteredUsers : users).filter(
    (user) => {
      const createdDate = new Date(user.createdAt || "");
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      return createdDate >= oneWeekAgo;
    },
  ).length;

  // Update useEffect to reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
    setTotalPages(Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  }, [searchQuery, filteredUsers.length]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await api.service(`users`).find();
        setUsers(response);
        setTotalPages(Math.ceil(response.length / ITEMS_PER_PAGE));
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to fetch users."),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error.message}
      </Alert>
    );
  }

  return (
    <ContentCard>
      <Text fontSize="2xl" fontWeight="bold" mb={6}>
        User Management Dashboard
      </Text>

      {/* Statistics Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Stat
          p={4}
          bg={bgCard}
          borderRadius="lg"
          boxShadow="sm"
          border="1px"
          borderColor={borderColor}
        >
          <StatLabel>Total Users</StatLabel>
          <StatNumber>{totalUsersFiltered}</StatNumber>
        </Stat>
        <Stat
          p={4}
          bg={bgCard}
          borderRadius="lg"
          boxShadow="sm"
          border="1px"
          borderColor={borderColor}
        >
          <StatLabel>Subscribed Users</StatLabel>
          <StatNumber>{subscribedUsersFiltered}</StatNumber>
        </Stat>
        <Stat
          p={4}
          bg={bgCard}
          borderRadius="lg"
          boxShadow="sm"
          border="1px"
          borderColor={borderColor}
        >
          <StatLabel>Unsubscribed Users</StatLabel>
          <StatNumber>{unsubscribedUsersFiltered}</StatNumber>
        </Stat>
        <Stat
          p={4}
          bg={bgCard}
          borderRadius="lg"
          boxShadow="sm"
          border="1px"
          borderColor={borderColor}
        >
          <StatLabel>New Users (Last 7 days)</StatLabel>
          <StatNumber>{newUsersFiltered}</StatNumber>
        </Stat>
      </SimpleGrid>

      {/* Add Search Input */}
      <InputGroup mb={6}>
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by name, email, account type, or status..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          bg={bgCard}
          borderColor={borderColor}
        />
      </InputGroup>
      {/* Users Table */}
      <Box
        bg={bgCard}
        borderRadius="lg"
        boxShadow="sm"
        border="1px"
        borderColor={borderColor}
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Account Type</Th>
              <Th>Status</Th>
              <Th>Created At</Th>
            </Tr>
          </Thead>
          <Tbody>
            {getPaginatedData().map((user) => (
              <Tr key={user.id}>
                <Td>{`${user.firstName} ${user.lastName}`}</Td>
                <Td>{user.email}</Td>
                <Td>
                  <Text textTransform="capitalize">
                    {user.credit ? (
                      <Badge colorScheme="green">Subscribed</Badge>
                    ) : (
                      <Badge colorScheme="red">Unsubscribed</Badge>
                    )}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={user.isActive ? "green" : "red"}>
                    {user.isActive ? "Active" : "Inactive"}
                  </Badge>
                </Td>
                <Td>{dateToString(user.createdAt || "")}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Update Pagination to show "No results" when filtered data is empty */}
        {filteredUsers.length > 0 ? (
          <Flex justify="center" p={4}>
            <Button
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              mr={2}
            >
              Previous
            </Button>
            <Text alignSelf="center" mx={4}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              ml={2}
            >
              Next
            </Button>
          </Flex>
        ) : (
          <Flex justify="center" p={4}>
            <Text color="gray.500">No users found matching your search.</Text>
          </Flex>
        )}
      </Box>
    </ContentCard>
  );
};
